import { EvidenceSchema } from './chunk-43SJKO57.js';
import { z } from 'zod';

var SemanticActorSchema = z.object({ "role": z.enum(["initiator", "sender", "receiver", "relayer", "spender", "owner", "contract", "counterparty", "instance", "other"]).describe("The role an address plays in a semantic node.").default("other"), "address": z.string().describe("Lowercase 0x-prefixed address."), "chain_id": z.union([z.string(), z.null()]).describe("Chain the address lives on. Differs from the tx chain for the destination side of a bridge node.").default(null), "entity_id": z.union([z.string(), z.null()]).describe("Resolved catalog entity id (populated post-decode).").default(null), "label": z.union([z.string(), z.null()]).describe("Human label/name for the address, if known.").default(null) }).describe("An address participating in a semantic node, with optional\ncatalog/entity decoration filled in by the pipeline annotate step.");
var SemanticAssetSchema = z.object({ "token_address": z.union([z.string(), z.null()]).describe("Token contract; None / 'native' for the chain's gas token.").default(null), "symbol": z.union([z.string(), z.null()]).describe("e.g. 'LINK', 'USDC'.").default(null), "decimals": z.union([z.number().int().gte(0).lte(36), z.null()]).default(null), "amount_raw": z.union([z.string(), z.null()]).describe("Unscaled integer amount as a decimal string (no precision loss).").default(null), "token_id": z.union([z.string(), z.null()]).describe("The id of a non-fungible (ERC-721) or multi-token (ERC-1155) asset, as a decimal string. ``amount_raw`` is then the count of that id (1 for an ERC-721) \u2014 the same convention as ``TokenFlow.token_id``.").default(null), "amount": z.union([z.string(), z.null()]).describe("Human-formatted amount ('2747.8') when decimals are known.").default(null), "usd": z.union([z.string(), z.null()]).describe("USD value as a string, when a price was available.").default(null), "direction": z.union([z.string(), z.null()]).describe("'in' | 'out' relative to the node's initiator.").default(null), "basis": z.union([z.string(), z.null()]).describe("What ``amount`` represents (an :class:`AmountBasis` value): 'actual' (the settled amount, the default) or 'quoted_minimum' (a swap min-return floor read from the call). None == unspecified.").default(null), "min_amount_raw": z.union([z.string(), z.null()]).describe("The quoted-minimum (floor) as an unscaled integer string, present only when ``amount`` is the reconciled ACTUAL and a floor was also known \u2014 so the UI can render 'actual (min: floor)'.").default(null), "min_amount": z.union([z.string(), z.null()]).describe("Human-formatted floor matching ``min_amount_raw``.").default(null), "max_amount_raw": z.union([z.string(), z.null()]).describe("Quoted spending ceiling in base units, separate from the settled amount.").optional(), "max_amount": z.union([z.string(), z.null()]).describe("Human-formatted ceiling matching ``max_amount_raw``.").optional(), "impersonates": z.union([z.record(z.string(), z.any()), z.null()]).describe("The well-known asset this token CLAIMS to be and is not (``commons.tokens.impersonation.Impersonation.to_dict``): its symbol is still what the token calls itself \u2014 the evidence \u2014 but no title or label may show it bare. Absent for an honest token.").default(null) }).describe("A value movement expressed in human units where possible.");
var SemanticSourceRefSchema = z.object({ "ref_type": z.string().describe("'top_call' | 'log' | 'internal_tx' | 'nested_calldata'."), "address": z.union([z.string(), z.null()]).default(null), "log_index": z.union([z.number().int(), z.null()]).default(null), "internal_tx_index": z.union([z.number().int(), z.null()]).default(null), "path": z.array(z.string()).describe("Param path walked into nested calldata (e.g. ['message', 'data'] or ['commands', '2']).").optional() }).describe("A back-link from a semantic node to the raw decode artifact it was\nderived from, so a UI can drill down to the EVM-level detail.");
var SEMANTIC_NODE_KINDS = [
  "proposal",
  "message",
  "envelope",
  "payload",
  "action",
  "batch",
  "multicall",
  "transfer",
  "swap",
  "order_place",
  "arbitrage",
  "approval",
  "deposit",
  "withdraw",
  "mint",
  "burn",
  "ownership_transfer",
  "contract_deploy",
  "instance_create",
  "account_close",
  "account_delegation",
  "bridge_send",
  "bridge_receive",
  "governance_execute",
  "safe_execution",
  "user_operation",
  "unknown"
];
var SEMANTIC_ACTOR_ROLES = [
  "initiator",
  "sender",
  "receiver",
  "relayer",
  "spender",
  "owner",
  "contract",
  "counterparty",
  "other"
];
var AMOUNT_BASIS = {
  ACTUAL: "actual",
  QUOTED_MINIMUM: "quoted_minimum"
};
var ORDER_STATUS = {
  OPEN: "open",
  FILLED: "filled"
};
var SemanticNodeSchema = z.lazy(
  () => z.object({
    id: z.string(),
    kind: z.string().default("unknown"),
    title: z.string(),
    protocol_id: z.union([z.string(), z.null()]).default(null),
    category: z.union([z.string(), z.null()]).default(null),
    chain_id: z.union([z.string(), z.null()]).default(null),
    actors: z.array(SemanticActorSchema).default([]),
    assets: z.array(SemanticAssetSchema).default([]),
    attributes: z.record(z.string(), z.unknown()).default({}),
    children: z.array(SemanticNodeSchema).default([]),
    source_refs: z.array(SemanticSourceRefSchema).default([]),
    confidence: z.number().min(0).max(1).default(1),
    evidence: z.union([EvidenceSchema, z.null()]).default(null),
    proxy_chain: z.array(z.string()).default([])
  })
);
var SemanticTreeSchema = z.object({
  version: z.string().default("1"),
  summary: z.union([z.string(), z.null()]).default(null),
  roots: z.array(SemanticNodeSchema).default([])
});
function walkSemanticTree(tree) {
  const out = [];
  const visit = (node) => {
    out.push(node);
    for (const child of node.children) visit(child);
  };
  for (const root of tree.roots) visit(root);
  return out;
}

export { AMOUNT_BASIS, ORDER_STATUS, SEMANTIC_ACTOR_ROLES, SEMANTIC_NODE_KINDS, SemanticActorSchema, SemanticAssetSchema, SemanticNodeSchema, SemanticSourceRefSchema, SemanticTreeSchema, walkSemanticTree };
//# sourceMappingURL=chunk-77DGXD3H.js.map
//# sourceMappingURL=chunk-77DGXD3H.js.map